import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import { calculateDistance } from './utils';
import config from "../config";
import BackButton from './BackButton';
import PoiCardLite from './poicardlite';
// import markerIcon from '../assets/images/nody_pin.svg'; // RIMOSSO - ora generato dinamicamente
import 'leaflet/dist/leaflet.css';
import useTranslation from './useTranslation';
import authService from '../services/auth';
import LocalIcon from './LocalIcon';
import { getOptimizedPoiImage } from '../utils/imageUtils';

const NEARBY_FILTER = "all";
const ITALY_CENTER = [41.9028, 12.4964];
const DEFAULT_ZOOM = 6;
const PADDING = 24;
const VERTICAL_OFFSET = -120;
const ANIMATION_DELAY = 100;

const calculateMarkerSize = (zoom) => {
  const maxSize = { width: 50, height: 60 };
  
  if (zoom >= 15) return {
    size: maxSize,
    anchor: {
      x: maxSize.width / 2,
      y: maxSize.height
    }
  };

  const zoomDiff = 15 - zoom;
  const reductionFactor = Math.max(0.2, 1 - (zoomDiff * 0.04));
  
  const size = {
    width: Math.round(maxSize.width * reductionFactor),
    height: Math.round(maxSize.height * reductionFactor)
  };

  return {
    size,
    anchor: {
      x: Math.round(size.width / 2),
      y: size.height
    }
  };
};

const Map = () => {
  const [pois, setPois] = useState([]);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [visiblePois, setVisiblePois] = useState([]);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialPoiDetailLoad, setInitialPoiDetailLoad] = useState(true);
  const [cardState, setCardState] = useState('hidden');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserMarkerSelected, setIsUserMarkerSelected] = useState(false);

  const loadedTaxonomyIds = useRef(new Set());
  const mapRef = useRef(null);
  const sliderRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedLang } = useTranslation();

  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) return null;

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (isFinite(latitude) && isFinite(longitude)) {
          setUserLocation({ latitude, longitude });
        }
      },
      (error) => console.error('Errore geolocalizzazione:', error),
      options
    );

    return watchId;
  }, []);

  const loadTaxonomies = useCallback(async (pois) => {
    const taxonomyRequests = pois.reduce((acc, poi) => {
      const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
      
      if (taxonomyId && !loadedTaxonomyIds.current.has(taxonomyId)) {
        acc.push(
          authService.get(`${config.API_URL}/taxonomy/term/${taxonomyId}?_format=json`)
            .then(response => {
              const term = response.data;
              return {
                id: term.tid[0].value,
                title: term.name[0].value,
                icon: term.field_icona?.[0]?.url || null,
                pin: term.field_pin?.[0]?.url || null,
                field_colore: term.field_colore || [], 
              };
            })
        );
      }
      return acc;
    }, []);

    try {
      const taxonomyResults = await Promise.all(taxonomyRequests);
      const newTaxonomyData = taxonomyResults.reduce((acc, taxonomy) => {
        acc[taxonomy.id] = taxonomy;
        return acc;
      }, {});

      setTaxonomyData(prev => ({ ...prev, ...newTaxonomyData }));
    } catch (error) {
      console.error('Errore caricamento tassonomie:', error);
    }
  }, []);

  const calculateFilteredPois = useCallback((selectedPOI, allPois, maxDistance) => {
    if (!selectedPOI?.field_geolocalizzazione?.[0] || !allPois?.length) return [];
    
    const { lat: selectedLat, lng: selectedLng } = selectedPOI.field_geolocalizzazione[0];
    if (!isFinite(selectedLat) || !isFinite(selectedLng)) return [];

    const poisWithDistance = allPois
      .map(poi => {
        if (!poi.field_geolocalizzazione?.[0]) return null;
        const { lat, lng } = poi.field_geolocalizzazione[0];
        if (!isFinite(lat) || !isFinite(lng)) return null;
        const distance = calculateDistance(selectedLat, selectedLng, lat, lng);
        return { ...poi, distance };
      })
      .filter(Boolean);

    const filteredPois = maxDistance === "all" 
      ? poisWithDistance 
      : poisWithDistance.filter(poi => poi.distance <= parseFloat(maxDistance));

    return filteredPois.sort((a, b) => {
      if (a.nid[0].value === selectedPOI.nid[0].value) return -1;
      if (b.nid[0].value === selectedPOI.nid[0].value) return 1;
      return a.distance - b.distance;
    });
  }, []);

  const createUserIcon = useCallback((isSelected = false) => {
    const zoom = mapRef.current ? mapRef.current.getZoom() : DEFAULT_ZOOM;
    const markerDimensions = calculateMarkerSize(zoom);
    
    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;

    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 719.4 842">
        <circle cx="362.7" cy="360.7" r="317.2" fill="#FF5555"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="#FF3333" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="#FF3333"/>
        <path d="M362.7 260 q-25 0-42.5-17.5 t-17.5-42.5 q0-25 17.5-42.5 t42.5-17.5 q25 0 42.5 17.5 t17.5 42.5 q0 25-17.5 42.5 t-42.5 17.5 Z" fill="#FFFFFF"/>
        <path d="M302.7 550 v-140 h-40 v-120 q0-25 17.5-42.5 t42.5-17.5 h80 q25 0 42.5 17.5 t17.5 42.5 v120 h-40 v140 Z" fill="#FFFFFF" transform="translate(0, 40)"/>
      </svg>`;

    return L.divIcon({
      html: svgString,
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      className: 'map-marker user-location-marker'
    });
  }, []);

  const handleMarkerClick = useCallback((poi) => {
    if (!mapRef.current || !poi.field_geolocalizzazione?.[0]) return;
    if (selectedPOI && selectedPOI.nid[0].value === poi.nid[0].value) return;

    const map = mapRef.current;
    const { lat, lng } = poi.field_geolocalizzazione[0];
    
    if (!isFinite(lat) || !isFinite(lng)) return;

    if (selectedPOI) {
      setSelectedPOI(null);
      setVisiblePois([]);
      setCardState('hidden');
      
      requestAnimationFrame(() => {
        setSelectedPOI(poi);
        const filtered = calculateFilteredPois(poi, pois, NEARBY_FILTER);
        setVisiblePois(filtered);
        setActiveCardIndex(0);
        setCardState('visible');
        if (sliderRef.current) sliderRef.current.scrollLeft = 0;

        const pixelCenter = map.latLngToContainerPoint([lat, lng]);
        const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
        const newCenter = map.containerPointToLatLng(newPixelCenter);
        map.setView(newCenter, map.getZoom(), { animate: true });
      });
    } else {
      setCardState('entering');
      requestAnimationFrame(() => {
        setSelectedPOI(poi);
        const filtered = calculateFilteredPois(poi, pois, NEARBY_FILTER);
        setVisiblePois(filtered);
        setActiveCardIndex(0);
        if (sliderRef.current) sliderRef.current.scrollLeft = 0;

        setTimeout(() => {
          setCardState('visible');
        }, 10);

        const pixelCenter = map.latLngToContainerPoint([lat, lng]);
        const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
        const newCenter = map.containerPointToLatLng(newPixelCenter);
        map.setView(newCenter, map.getZoom(), { animate: true });
      });
    }
  }, [selectedPOI, pois, calculateFilteredPois]);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const scrollPosition = slider.scrollLeft;
    const cardElement = slider.querySelector('.card-container');
    if (!cardElement) return;
    
    const cardWidth = cardElement.getBoundingClientRect().width;
    const newIndex = Math.round(scrollPosition / cardWidth);
    
    setCanScrollLeft(scrollPosition > 0);
    setCanScrollRight(scrollPosition < slider.scrollWidth - slider.clientWidth - 1);

    if (newIndex >= 0 && newIndex < visiblePois.length && newIndex !== activeCardIndex) {
      const newSelectedPoi = visiblePois[newIndex];
      setActiveCardIndex(newIndex);
      
      if (mapRef.current && newSelectedPoi?.field_geolocalizzazione?.[0]) {
        const { lat, lng } = newSelectedPoi.field_geolocalizzazione[0];
        if (isFinite(lat) && isFinite(lng)) {
          const map = mapRef.current;
          setSelectedPOI(newSelectedPoi);
          
          const pixelCenter = map.latLngToContainerPoint([lat, lng]);
          const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
          const newCenter = map.containerPointToLatLng(newPixelCenter);
          
          map.setView(newCenter, map.getZoom(), { 
            animate: true,
            duration: 0.5
          });
        }
      }
    }
  }, [activeCardIndex, visiblePois]);

  const handleClosePanel = useCallback(() => {
    setCardState('exiting');
    setTimeout(() => {
      setSelectedPOI(null);
      setVisiblePois([]);
      setCardState('hidden');
    }, ANIMATION_DELAY);
  }, []);

  const scrollToIndex = useCallback((index) => {
    if (!sliderRef.current || index < 0 || index >= visiblePois.length) return;
    
    const slider = sliderRef.current;
    const cardElement = slider.querySelector('.card-container');
    if (!cardElement) return;
    
    const cardWidth = cardElement.getBoundingClientRect().width;
    const scrollPosition = cardWidth * index;
    
    slider.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }, [visiblePois.length]);

  const createPoiIcon = useCallback((poi, isSelected) => {
    if (!mapRef.current) return L.icon({
      iconUrl: null, // Fallback - dovrebbe mai essere usato
      iconSize: [50, 60],
      iconAnchor: [25, 60]
    });
  
    const zoom = mapRef.current.getZoom();
    const markerDimensions = calculateMarkerSize(zoom);
    const uniqueId = `poi-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
    const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
    const taxonomy = taxonomyId ? taxonomyData[taxonomyId] : null;
    const color = taxonomy?.field_colore?.[0]?.color || '#1E88E5';
    const poiImage = getOptimizedPoiImage(poi, 'thumbnail', null); // Fallback gestito internamente
  
    const size = isSelected ? {
      width: Math.round(markerDimensions.size.width * 2),
      height: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.size;
  
    const anchor = isSelected ? {
      x: Math.round(markerDimensions.size.width * 2 / 2),
      y: Math.round(markerDimensions.size.height * 2)
    } : markerDimensions.anchor;
  
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 719.4 842"><defs><pattern id="imagePattern-${uniqueId}" patternUnits="userSpaceOnUse" width="634.4" height="634.4" x="45.5" y="43.5"><image x="30" y="-50" width="80%" height="80%" href="${poiImage}" preserveAspectRatio="xMidYMid slice"/></pattern></defs><circle cx="362.7" cy="360.7" r="317.2" fill="url(#imagePattern-${uniqueId})"/><circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="${color}" stroke-width="60"/><polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="${color}"/></svg>`;
  
    return L.divIcon({
      html: svgString,
      iconSize: [size.width, size.height],
      iconAnchor: [anchor.x, anchor.y],
      className: 'map-marker'
    });
  }, [taxonomyData]);

  const MapController = React.memo(() => {
    const map = useMap();
  
    useEffect(() => {
      mapRef.current = map;
      
      const handleZoomAnim = (e) => setMapZoom(e.zoom);
      const handleZoom = () => setMapZoom(map.getZoom());
      
      map.on('zoomanim', handleZoomAnim);
      map.on('zoom', handleZoom);
      
      if (isInitialLoad && mapBounds && !location.state?.selectedPOI) {
        if (mapBounds.isValid()) {
          map.fitBounds(mapBounds, { 
            padding: [PADDING, PADDING],
            maxZoom: 19
          });
        }
        setIsInitialLoad(false);
      }
  
      if (location.state?.selectedPOI && selectedPOI && initialPoiDetailLoad) {
        const { lat, lng } = selectedPOI.field_geolocalizzazione[0];
        
        if (isFinite(lat) && isFinite(lng)) {
          map.setView([lat, lng], 17, { animate: false });
          
          setTimeout(() => {
            const pixelCenter = map.latLngToContainerPoint([lat, lng]);
            const newPixelCenter = pixelCenter.subtract([0, VERTICAL_OFFSET]);
            const newCenter = map.containerPointToLatLng(newPixelCenter);
            
            if (isFinite(newCenter.lat) && isFinite(newCenter.lng)) {
              map.setView(newCenter, 17, { 
                animate: true,
                duration: 0.5
              });
            }
          }, ANIMATION_DELAY);
        }
        
        setInitialPoiDetailLoad(false);
        setCardState('visible');
      }

      return () => {
        map.off('zoomanim', handleZoomAnim);
        map.off('zoom', handleZoom);
      };
    }, [map]);

    return null;
  });

  const renderPoiCard = useCallback((poi) => (
    <div key={poi.nid[0].value} className="card-container card-containerlite px-1">
      <PoiCardLite 
        poi={poi}
        taxonomyData={taxonomyData}
        distance={userLocation && poi.field_geolocalizzazione?.[0] ?
          `${calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            poi.field_geolocalizzazione[0].lat,
            poi.field_geolocalizzazione[0].lng
          ).toFixed(1)} km` :
          '...'
        }
        onClose={handleClosePanel}
        handleNavigate={() => navigate(`/poi/${poi.nid[0].value}`)}
      />
    </div>
  ), [handleClosePanel, navigate, taxonomyData, userLocation]);

  useEffect(() => {
    const watchId = fetchUserLocation();
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authService.get(config.API_ENDPOINTS.getPOIList(selectedLang));
        const validPois = response.filter(poi => {
          const coords = poi.field_geolocalizzazione?.[0];
          return coords && isFinite(coords.lat) && isFinite(coords.lng);
        });
        
        setPois(validPois);
        await loadTaxonomies(validPois);

        const validCoordinates = validPois
          .map(poi => poi.field_geolocalizzazione[0])
          .filter(coords => coords && isFinite(coords.lat) && isFinite(coords.lng))
          .map(({ lat, lng }) => [lat, lng]);

        if (validCoordinates.length > 0) {
          const bounds = L.latLngBounds(validCoordinates);
          setMapBounds(bounds);
        }

        if (location.state?.selectedPOI) {
          const selectedPoi = location.state.selectedPOI;
          if (selectedPoi.field_geolocalizzazione?.[0]) {
            setSelectedPOI(selectedPoi);
            const filtered = calculateFilteredPois(selectedPoi, validPois, NEARBY_FILTER);
            setVisiblePois(filtered);
            setCardState('visible');
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Errore durante il recupero dei dati:', error);
        setError('Errore durante il caricamento dei dati');
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [fetchUserLocation, location.state, calculateFilteredPois, loadTaxonomies, selectedLang]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    handleScroll();
    slider.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  if (loading) return <div class="m-3">Caricamento...</div>;
  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  return (
    <div style={{ height: 'calc(100dvh - 140px)', scrollSnapAlign: 'start', position: 'relative' }}>
      <MapContainer 
        center={ITALY_CENTER} 
        zoom={DEFAULT_ZOOM} 
        id="map" 
        zoomControl={false}
        ref={mapRef}
      >
        <MapController />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          minZoom={5}
          maxZoom={19}
        />
        
        <BackButton className="goback-map"/>

        <button
          aria-label="La mia posizione"
          className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
          style={{position: 'absolute', top: '10px', right: '8px', left: 'inherit', zIndex: 1000, width: '40px', height: '40px'}}
          onClick={() => {
            if (userLocation && mapRef.current) {
              const map = mapRef.current;
              const { latitude, longitude } = userLocation;
              
              setIsUserMarkerSelected(true);
              setTimeout(() => setIsUserMarkerSelected(false), 2000);
              
              const pixelCenter = map.latLngToContainerPoint([latitude, longitude]);
              const newPixelCenter = pixelCenter.subtract([0, (VERTICAL_OFFSET + 100)]);
              const newCenter = map.containerPointToLatLng(newPixelCenter);
              map.setView(newCenter, map.getZoom(), { animate: true });
              
              handleClosePanel();
              setSelectedPOI(null);
            }
          }}
        >
          <LocalIcon icon="my_location" style={{ fontSize: '16px' }} />
        </button>
        
        {pois.map((poi) => {
          const latLng = poi.field_geolocalizzazione?.[0];
          if (!latLng || !isFinite(latLng.lat) || !isFinite(latLng.lng)) return null;
          
          const { lat, lng } = latLng;
          const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
          const isSelected = selectedPOI && selectedPOI.nid[0].value === poi.nid[0].value;

          return (
            <Marker
              key={poi.nid[0].value}
              position={[lat, lng]}
              icon={createPoiIcon(poi, isSelected)}
              eventHandlers={{
                click: () => handleMarkerClick(poi),
              }}
              zIndexOffset={isSelected ? 1000 : 0}
            />
          );
        })}
        
        {userLocation && isFinite(userLocation.latitude) && isFinite(userLocation.longitude) && mapRef.current && (
          <Marker
            position={[userLocation.latitude, userLocation.longitude]}
            icon={createUserIcon(isUserMarkerSelected)}
            interactive={false}
          />
        )}
      </MapContainer>

      {selectedPOI && (
        <div className={`map-card ${cardState}`}>
          <div className="position-relative">
            {canScrollLeft && (
              <button
                className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute start-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
                onClick={() => scrollToIndex(activeCardIndex - 1)}
                aria-label="Previous"
              >
                <LocalIcon icon="chevron_left" style={{ fontSize: '16px' }} />
              </button>
            )}
            
            {canScrollRight && (
              <button
                className="btn bg-white border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
                onClick={() => scrollToIndex(activeCardIndex + 1)}
                aria-label="Next"
              >
                <LocalIcon icon="chevron_right" style={{ fontSize: '16px' }} />
              </button>
            )}

            <div 
              ref={sliderRef}
              className="row flex-nowrap carousel-container"
              style={{
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                position: 'relative',
                padding: '0 8px'
              }}
            >
              {visiblePois.map(renderPoiCard)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;